import { useSelector } from '@/api/state';
import { SendSimpleCustomEmailControl } from '@/ui/components/sendSimpleCustomEmailControl/SendSimpleCustomEmailControl';
import { getCustomer } from '@/utils/stateAccessors';
import { AnchorButton, Button } from '@payaca/ambrosia';
import ContentPanel from '@payaca/components/contentPanel/ContentPanel';
import DropdownField from '@payaca/components/dropdownField/DropdownField';
import {
  InputColourVariant,
  InputStyleVariant,
} from '@payaca/components/inputWrapper/InputWrapper';
import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import { getUrlSearchParam } from '@payaca/helpers/urlHelper';
import * as customerActions from '@payaca/store/customer/customerActions';
import * as documentActions from '@payaca/store/documents/documentActions';
import * as formActions from '@payaca/store/forms/formsActions';
import { FormCategoryType } from '@payaca/types/formTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { Link, useLocation } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import useGetProjectByIdOverview from '../../../api/queries/project/useGetProjectByIdOverview';
import './FormPage.sass';

const optionGroups = [
  { name: FormCategoryType.GENERAL, id: 1 },
  { name: FormCategoryType.HEATING, id: 2 },
  { name: FormCategoryType.ELECTRICAL, id: 3 },
];
const SEND_FORM_EMAIL_TEMPLATE = `
Hi,

Here is a form for you to complete. Please work through all the questions.

[button]

When you're done, there is a button you can press to send it back to us.

Any problems, feel free to get in touch directly.

Thanks,
[company_name]
`;

const FormCreatePage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const [selectedFormId, setSelectedFormId] = useState(2); // default initial job report
  const [sendForm, setSendForm] = useState(false);
  const [formRecipientData, setFormRecipientData] = useState<any>();
  const [canSendForm, setCanSendForm] = useState(false);
  const [isFormSent, setIsFormSent] = useState(false);

  const parentFolderId =
    getUrlSearchParam(location.search, 'parentFolderId') ?? undefined;
  const isCreatingDocument = useSelector(
    (state) => state.documents.isCreatingDocument
  );
  const projectId = useMemo(
    () => getUrlSearchParam(location.search, 'projectId') ?? undefined,
    [location]
  );
  const { project } = useGetProjectByIdOverview(projectId);

  const customer = useSelector((state) =>
    project?.customer?.id ? getCustomer(state, +project.customer.id) : undefined
  );
  const account = useSelector(
    (state: any) => state.users.myProfile.accounts[0]
  );

  const isFromMobile = useMemo(
    () => !!getUrlSearchParam(location.search, 'oneTimeLoginToken'),
    [location]
  );
  const { update } = useIntercom();

  useEffect(() => {
    // get available forms
    dispatch(formActions.requestGetAvailableForms());
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  const availableForms = useSelector((state) => state.forms.availableForms);

  const regionalFormOptions = useMemo(
    () =>
      availableForms
        .filter((form) => !form.archivedAt)
        .filter((form) => form.type !== 'pdf')
        .filter((form: any) => form?.regions.includes(account.region))
        .filter((form: any) => {
          if (
            import.meta.env.VITE_APP_ENV === 'development' ||
            (import.meta.env.VITE_ENV === 'staging' && account.id === 1020) // forms@payaca.com
          ) {
            return true;
          }
          return form?.accounts?.length
            ? form?.accounts?.includes(account.id)
            : true;
        })
        .map(({ label, value, category }) => {
          const optionGroup = optionGroups.find(
            (optionGroup) => optionGroup.name === category
          );
          return {
            label,
            value,
            optionGroupId: optionGroup ? optionGroup.id : 1,
          };
        }),
    [account, availableForms]
  );

  useEffect(() => {
    if (!projectId) {
      history.push('/dashboard');
    }
  }, [projectId, history]);

  useEffect(() => {
    update({
      hideDefaultLauncher: true,
    });

    dispatch(documentActions.clearDocuments());
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'dispatch', and 'update'
  }, [projectId]);

  useEffect(() => {
    if (project?.customer?.id) {
      dispatch(
        customerActions.requestGetAndSetCurrentCustomer(+project.customer.id)
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [project]);

  const handleFormCreated = useCallback(
    (formInstance: any) => {
      if (sendForm) {
        dispatch(
          formActions.requestSendForm(
            formInstance.id,
            formRecipientData.sendToEmail,
            formRecipientData.emailBody,
            formRecipientData.sendACopy
          )
        );
        return setIsFormSent(true);
      }
      history.push(
        `/forms/${formInstance.previewToken}/0?dealId=${project?.id}${
          isFromMobile ? '&isFromMobile=true' : ''
        }`
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    [history, project, isFromMobile, sendForm, formRecipientData]
  );

  const handleCreateOrSendForm = useCallback(() => {
    if (project?.id) {
      dispatch(
        documentActions.requestCreateDocument(
          selectedFormId,
          +project.id,
          sendForm,
          parentFolderId,
          handleFormCreated
        )
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [selectedFormId, project, handleFormCreated, sendForm, parentFolderId]);

  const selectedForm = availableForms.find(
    (form) => form.value === selectedFormId
  );

  return (
    <ResponsiveViewWrapper
      className={'form-page-container filled'}
      downBreakpointSm={900}
    >
      <div className={'form-page-wrapper'}>
        <div className={'form-page-header'}>
          <h3>Forms</h3>
          <div className={'company-brand'}>
            <img className="logo" src={account.logoUrl} />{' '}
            <span>{account.companyName}</span>
          </div>
        </div>
        <div className={'form-body'}>
          <div className={'form-page-section-container'}>
            <div className={'form-page-section-content'}>
              <ContentPanel className={'form-page-create-container'}>
                <div>
                  {isFormSent ? (
                    <div className={'form-sent-container'}>
                      <UntitledIcon name="check-square.3" />
                      <h4>Your form has been sent!</h4>
                      <p>{`We'll send you an e-mail when it has been completed. In the meantime, you can review how it's progressing by checking your project.`}</p>
                      <AnchorButton as={Link} to={`/projects/${projectId}`}>
                        Return to project
                      </AnchorButton>
                    </div>
                  ) : (
                    <>
                      <div className={'form-element'}>
                        <h4>Which form would you like to complete:</h4>
                        <DropdownField
                          value={selectedFormId}
                          name="selectedFormId"
                          options={regionalFormOptions}
                          onChange={({ selectedFormId }: any) => {
                            setSelectedFormId(selectedFormId);
                          }}
                          styleVariant={InputStyleVariant.OUTSIZE}
                          colourVariant={InputColourVariant.SECONDARY}
                          hasBoxShadow={false}
                          optionGroups={optionGroups}
                        />
                      </div>
                      {selectedForm?.type === 'legacy' && (
                        <div className={'form-element'}>
                          <h4>How will this form be completed?</h4>
                          <div className={'form-send-config-container'}>
                            <div className={'form-radio-options-container'}>
                              <div
                                className={
                                  'form-radio-option ' +
                                  (!sendForm ? 'selected' : '')
                                }
                                onClick={() => setSendForm(false)}
                              >
                                I will complete this form
                              </div>
                              <div
                                className={
                                  'form-radio-option ' +
                                  (sendForm ? 'selected' : '')
                                }
                                onClick={() => {
                                  const selectedForm = availableForms.find(
                                    (form) => form.value === selectedFormId
                                  );
                                  if (selectedForm?.type === 'legacy') {
                                    setSendForm(true);
                                  }
                                }}
                              >
                                Send to someone else
                              </div>
                            </div>
                            <div
                              className={
                                'form-send-config-control ' +
                                (sendForm ? '' : 'hidden')
                              }
                            >
                              <SendSimpleCustomEmailControl
                                customer={customer}
                                allowCustomEmailTemplate={true}
                                requiredEmailBodyVariables={['button']}
                                initialSubject={`${account.companyName} has sent you a form`}
                                initialEmailBody={SEND_FORM_EMAIL_TEMPLATE.replace(
                                  '[company_name]',
                                  account.companyName
                                ).trim()}
                                onChange={(data) => setFormRecipientData(data)}
                                onValidityChange={(isValid: boolean) =>
                                  setCanSendForm(isValid)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </ContentPanel>
            </div>
          </div>
          {!isFormSent && (
            <div className={'form-navigation-options'}>
              <Button
                isProcessing={isCreatingDocument}
                replaceContentWhenProcessing
                onClick={handleCreateOrSendForm}
                disabled={sendForm && !canSendForm}
              >
                {sendForm ? 'Send form' : 'Create form'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </ResponsiveViewWrapper>
  );
};

export default FormCreatePage;
